import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useState, useEffect } from "react";
import { List, X, ForkKnife } from "@phosphor-icons/react";
import api from "@/lib/api";
import GlobalSearch from "@/components/GlobalSearch";

// Auto-detect iOS so we can surface the share-sheet setup CTA only to users who actually need it.
// On non-iOS, the link is still available in the Footer + How To Use page; we just don't shout about it here.
const IS_IOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

// Mobile-menu extras (hidden on desktop, also in the Footer).
const NAV_EXTRAS = [
  { to: "/how-to-use", label: "How To Use" },
  { to: "/faqs", label: "FAQs" },
  { to: "/conversions", label: "Conversions" },
];

// Items that are hidden from the DESKTOP top-nav because the row was
// getting too crowded (2026-02-26 user request). They remain visible in
// the mobile burger menu AND are already surfaced in the Footer, so
// nothing is actually removed from the app.
const DESKTOP_HIDDEN_PATHS = new Set(["/how-to-use", "/faqs", "/conversions"]);

export default function Header() {
  const { user, logout } = useAuth();
  const { flags } = useSiteFlags();
  const isStaff = ["Admin", "SuperAdmin"].includes(user?.role);
  const showPlanner = flags.meal_planner_enabled || isStaff;
  const showRecipeCreator = flags.recipe_creator_enabled || isStaff;
  // Historical page redirects home when its flag is off, so no staff preview link.
  const showHistorical = !!flags.historical_recipes_enabled;
  const planner = showPlanner && { to: "/planner", label: "Meal planner" };
  const historical = showHistorical && { to: "/historical", label: "Historical" };
  const creator = showRecipeCreator && { to: "/recipe-creator", label: "Recipe Creator" };
  const kitchenHelp = { to: "/kitchen-help", label: "Kitchen Help" };
  const voice = flags.voice_nav_enabled && { to: "/voice", label: "Voice" };
  const ordered = user
    ? [
        { to: "/", label: "Home" }, { to: "/directory", label: "Directory" },
        { to: "/dashboard", label: "My Cookbooks" }, planner,
        { to: "/shopping", label: "Shopping list" }, historical, creator,
        voice, kitchenHelp, { to: "/news", label: "News" }, { to: "/shop", label: "Shop" },
      ]
    : [
        { to: "/", label: "Home" }, { to: "/directory", label: "Directory" },
        planner, historical, creator, kitchenHelp, voice,
        { to: "/news", label: "News" }, { to: "/shop", label: "Shop" },
        { to: "/pricing", label: "Pricing" },
      ];
  const NAV = [...ordered.filter(Boolean), { to: "/import", label: "Import" }, ...NAV_EXTRAS];
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-rf-cream/90 backdrop-blur-xl border-b border-rf-ink/10">
      <div className="max-w-[1500px] mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-home-link" className="flex items-center gap-2 group">
            {settings.brand_logo_url ? (
              <img src={settings.brand_logo_url} alt="Recipe Filer" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-rf-amber flex items-center justify-center group-hover:bg-rf-orange transition-colors">
                <ForkKnife size={22} weight="duotone" className="text-rf-ink" />
              </div>
            )}
            <span className="font-display font-black text-xl text-rf-ink tracking-tight">
              <span style={{ color: "#2C1E16" }}>Recipe</span><span style={{ color: "#EF8200" }}>Filer</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-0 flex-nowrap justify-start flex-1 min-w-0 ml-3 mr-3" data-testid="primary-nav">
            {NAV.filter((n) => !DESKTOP_HIDDEN_PATHS.has(n.to)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-1 2xl:px-2 py-1.5 rounded-full text-[13px] 2xl:text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-rf-orange"
                      : "text-rf-ink-soft hover:text-rf-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2 shrink-0">
            <GlobalSearch />
            {IS_IOS && (
              <Link
                to="/ios-share-setup"
                data-testid="header-ios-link"
                title="Add Recipe Filer to your iOS share sheet"
                className="text-rf-ink-soft hover:text-rf-orange text-xs font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full hover:bg-rf-cream-alt transition-colors"
                aria-label="iPhone share-sheet setup"
              >
                iOS
              </Link>
            )}
            {user ? (
              <>
                <Link to="/profile" data-testid="header-profile-link" className="text-sm font-semibold text-rf-ink-soft hover:text-rf-orange px-2">
                  Profile
                </Link>
                {(user.role === "Admin" || user.role === "SuperAdmin") && (
                  <Link to="/admin" data-testid="header-admin-link" className="btn-outline-ink !py-2.5 !px-5 text-sm">
                    Admin
                  </Link>
                )}
                <button
                  data-testid="header-logout-button"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="text-sm font-semibold text-rf-ink-soft hover:text-rf-orange"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-link" className="text-sm font-semibold text-rf-ink-soft hover:text-rf-ink px-3">
                  Sign in
                </Link>
                <Link to="/register" data-testid="header-register-link" className="btn-amber !py-2.5 !px-5 text-sm">
                  Start Free
                </Link>
              </>
            )}
          </div>

          {/* Mobile/tablet search — icon-only trigger sits just before the hamburger. */}
          <div className="xl:hidden ml-auto mr-1">
            <GlobalSearch />
          </div>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="xl:hidden p-2 -mr-2">
            {open ? <X size={28} /> : <List size={28} />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden py-2 border-t border-rf-ink/10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block py-1.5 px-2 font-semibold text-sm ${isActive ? "text-rf-orange" : "text-rf-ink"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="border-t border-rf-ink/10 my-2" />
            <Link
              onClick={() => setOpen(false)}
              to="/ios-share-setup"
              data-testid="mobile-ios-link"
              className="block py-1.5 px-2 font-semibold text-sm text-rf-ink-soft"
            >
              iOS Share Sheet
            </Link>
            <div className="border-t border-rf-ink/10 my-2" />
            {user ? (
              <>
                <Link onClick={() => setOpen(false)} to="/profile" className="block py-1.5 px-2 font-semibold text-sm text-rf-ink">Profile</Link>
                {(user.role === "Admin" || user.role === "SuperAdmin") && (
                  <Link
                    onClick={() => setOpen(false)}
                    to="/admin"
                    data-testid="mobile-admin-link"
                    className="block py-1.5 px-2 font-semibold text-sm text-rf-orange"
                  >
                    Admin panel
                  </Link>
                )}
                <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="block w-full text-left py-1.5 px-2 font-semibold text-sm text-rf-ink-soft">Sign out</button>
              </>
            ) : (
              <>
                <Link onClick={() => setOpen(false)} to="/login" className="block py-1.5 px-2 font-semibold text-sm text-rf-ink">Sign in</Link>
                <Link onClick={() => setOpen(false)} to="/register" className="block py-1.5 px-2 font-semibold text-sm text-rf-orange">Start Free</Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
